import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import FormField from "layouts/pages/account/components/FormField";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { getOneEventSiegeApi, getAllEventApi } from "api/events";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import { Radio, RadioGroup, FormControlLabel, FormControl } from "@mui/material";
import { getAllEventTitleApi } from "api/events";

const EMPTY_SIEGE_ROW = () => ({ siegeUId: "", numbersOfUse: 0, selectedSiegeItem: null, name: "" });
const EMPTY_DATE_GROUP = () => ({ selectedDateItem: null, siegeRows: [EMPTY_SIEGE_ROW()] });

function UpdateCouponFormFields({ formValues, initialValues }) {
  const [siegeData, setSiegeData] = useState([]);
  const [eventData, setEventData] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventName, setEventName] = useState("");
  const [coponCode, setCoponCode] = useState("");
  const [typeOfCopon, setTypeOfCopon] = useState("montant");
  const [reduction, setReduction] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState(initialValues?.status || false);
  const [isCinemaOrCirque, setIsCinemaOrCirque] = useState(false);
  const [eventDateOptions, setEventDateOptions] = useState([]);

  // cinema/cirque: date groups
  const [dateGroups, setDateGroups] = useState([EMPTY_DATE_GROUP()]);

  // non-cinema/cirque: flat siege list
  const [siegeFormDataValues, setSiegeFormDataValues] = useState([]);

  // ── Load event list ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAllEvents = async () => {
      try {
        const eventResponse = await getAllEventTitleApi();
        setEventData(eventResponse.map((el) => ({ title: el.eventTitle, id: el._id })));
      } catch (err) {
        console.error("Error fetching events:", err);
      }
    };
    fetchAllEvents();
  }, []);

  // ── Populate form from initialValues ──────────────────────────────────────
  useEffect(() => {
    if (!initialValues) return;

    setCoponCode(initialValues.coponCode || "");
    setTypeOfCopon(initialValues.typeOfCopon || "montant");
    setReduction(initialValues.reduction || "");
    setFromDate(
      initialValues.fromDate ? new Date(initialValues.fromDate).toISOString().split("T")[0] : ""
    );
    setToDate(
      initialValues.toDate ? new Date(initialValues.toDate).toISOString().split("T")[0] : ""
    );
    setStatus(initialValues.status !== undefined ? initialValues.status : false);

    if (initialValues.eventId?._id) {
      fetchSiegeData(initialValues.eventId._id, initialValues.siege);
      setEventName(initialValues.eventId.eventTitle);
      formValues({ eventId: initialValues.eventId._id });
    }

    // Non-cinema initialisation (cinema groups built inside fetchSiegeData)
    if (Array.isArray(initialValues.siege)) {
      const formatted = initialValues.siege.map((s) => ({
        siegeUId: s.id,
        numberOfUse: s.numberOfUse,
        name: s.name,
        selectedSiegeItem: { id: s.id, name: s.name },
        selectedTimeId: s.selectedTimeId || null,
      }));
      setSiegeFormDataValues(formatted);
      formValues({
        siege: formatted.map((s) => ({
          id: s.siegeUId,
          name: s.name,
          numberOfUse: s.numberOfUse,
          ...(s.selectedTimeId ? { selectedTimeId: s.selectedTimeId } : {}),
        })),
      });
    } else {
      setSiegeFormDataValues([]);
    }
  }, [initialValues]);

  // ── Resolve selected event in autocomplete once eventData loads ───────────
  useEffect(() => {
    if (initialValues?.eventId?._id && eventData.length > 0) {
      const found = eventData.find((e) => e.id === initialValues.eventId._id);
      setSelectedEvent(found || null);
    }
  }, [initialValues, eventData]);

  // ── Resolve selectedSiegeItem to full API objects (non-cinema) ────────────
  useEffect(() => {
    if (isCinemaOrCirque || siegeData.length === 0 || siegeFormDataValues.length === 0) return;
    const resolved = siegeFormDataValues.map((row) => {
      if (row.siegeUId && (!row.selectedSiegeItem || !row.selectedSiegeItem.fromGeneratedId)) {
        const match = siegeData.find((s) => s.id === row.siegeUId);
        if (match) return { ...row, selectedSiegeItem: match };
      }
      return row;
    });
    const changed = resolved.some((r, i) => r.selectedSiegeItem !== siegeFormDataValues[i].selectedSiegeItem);
    if (changed) setSiegeFormDataValues(resolved);
  }, [siegeData, isCinemaOrCirque]);

  // ── Resolve selectedSiegeItem inside dateGroups once siegeData loads ──────
  useEffect(() => {
    if (!isCinemaOrCirque || siegeData.length === 0) return;
    setDateGroups((prev) =>
      prev.map((g) => ({
        ...g,
        siegeRows: g.siegeRows.map((row) => {
          if (row.siegeUId && (!row.selectedSiegeItem || !row.selectedSiegeItem.fromGeneratedId)) {
            const match = siegeData.find((s) => s.id === row.siegeUId);
            if (match) return { ...row, selectedSiegeItem: match };
          }
          return row;
        }),
      }))
    );
  }, [siegeData, isCinemaOrCirque]);

  // ── Fetch siege data + build date groups for cinema/cirque ────────────────
  const fetchSiegeData = async (eventId, initialSiege) => {
    try {
      const details = await getOneEventSiegeApi(eventId);
      setSiegeData(details.eventSiege || []);

      const isCinema = details.isCinema || false;
      const isCirque = details.isCirque || false;
      setIsCinemaOrCirque(isCinema || isCirque);

      if (isCinema || isCirque) {
        const dates = (details.eventDateList || []).map((d) => ({
          id: d.generatedId,
          label: new Date(d.eventDate).toLocaleString("fr-FR", {
            timeZone: "UTC",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
        }));
        setEventDateOptions(dates);

        // Restore date groups from existing siege data
        if (Array.isArray(initialSiege) && initialSiege.length > 0) {
          const byTimeId = {};
          for (const s of initialSiege) {
            const key = s.selectedTimeId || "__none__";
            if (!byTimeId[key]) byTimeId[key] = [];
            byTimeId[key].push(s);
          }
          const groups = Object.entries(byTimeId).map(([timeId, sieges]) => ({
            selectedDateItem: dates.find((d) => d.id === timeId) || null,
            siegeRows: sieges.map((s) => ({
              siegeUId: s.id,
              numbersOfUse: s.numberOfUse,
              name: s.name,
              selectedSiegeItem: { id: s.id, name: s.name },
            })),
          }));
          setDateGroups(groups);
        } else {
          setDateGroups([EMPTY_DATE_GROUP()]);
        }
      }
    } catch (error) {
      console.error("Error fetching siege data:", error);
    }
  };

  // ── Cinema/cirque helpers ─────────────────────────────────────────────────

  const buildSiegeArrayFromGroups = (groups) =>
    groups.flatMap((g) =>
      g.siegeRows.map((row) => ({
        id: row.siegeUId,
        name: row.name,
        numberOfUse: row.numbersOfUse,
        ...(g.selectedDateItem ? { selectedTimeId: g.selectedDateItem.id } : {}),
      }))
    );

  const updateGroupsAndNotify = (updated) => {
    setDateGroups(updated);
    formValues({ siege: buildSiegeArrayFromGroups(updated) });
  };

  const handleDateGroupChange = (groupIndex, newValue) => {
    updateGroupsAndNotify(
      dateGroups.map((g, i) =>
        i === groupIndex ? { ...g, selectedDateItem: newValue, siegeRows: [EMPTY_SIEGE_ROW()] } : g
      )
    );
  };

  const handleAddDateGroup = () => updateGroupsAndNotify([...dateGroups, EMPTY_DATE_GROUP()]);

  const handleDeleteDateGroup = (groupIndex) =>
    updateGroupsAndNotify(dateGroups.filter((_, i) => i !== groupIndex));

  const handleGroupSiegeChange = (groupIndex, rowIndex, newValue) => {
    updateGroupsAndNotify(
      dateGroups.map((g, gi) => {
        if (gi !== groupIndex) return g;
        return {
          ...g,
          siegeRows: g.siegeRows.map((row, ri) =>
            ri === rowIndex
              ? { ...row, siegeUId: newValue?.id || "", name: newValue?.name || "", selectedSiegeItem: newValue }
              : row
          ),
        };
      })
    );
  };

  const handleGroupSiegeNumberOfUseChange = (groupIndex, rowIndex, e) => {
    const val = parseInt(e.target.value, 10) || 0;
    updateGroupsAndNotify(
      dateGroups.map((g, gi) => {
        if (gi !== groupIndex) return g;
        return {
          ...g,
          siegeRows: g.siegeRows.map((row, ri) => (ri === rowIndex ? { ...row, numbersOfUse: val } : row)),
        };
      })
    );
  };

  const handleAddSiegeToGroup = (groupIndex) =>
    updateGroupsAndNotify(
      dateGroups.map((g, gi) =>
        gi === groupIndex ? { ...g, siegeRows: [...g.siegeRows, EMPTY_SIEGE_ROW()] } : g
      )
    );

  const handleDeleteSiegeFromGroup = (groupIndex, rowIndex) =>
    updateGroupsAndNotify(
      dateGroups.map((g, gi) => {
        if (gi !== groupIndex) return g;
        return { ...g, siegeRows: g.siegeRows.filter((_, ri) => ri !== rowIndex) };
      })
    );

  // ── Non-cinema/cirque helpers ─────────────────────────────────────────────

  const buildSiegeArray = (values) =>
    values.map((s) => ({
      id: s.siegeUId,
      name: s.name,
      numberOfUse: s.numberOfUse,
      ...(s.selectedTimeId ? { selectedTimeId: s.selectedTimeId } : {}),
    }));

  const handleSiegeNumberOfUseInputChange = (index, event) => {
    const updated = siegeFormDataValues.map((item, i) =>
      i === index ? { ...item, numberOfUse: parseInt(event.target.value, 10) || 0 } : item
    );
    setSiegeFormDataValues(updated);
    formValues({ siege: buildSiegeArray(updated) });
  };

  const handleAutoCompleteSiegeChange = (index, event, newValue) => {
    setSiegeFormDataValues((prev) => {
      const updated = prev.map((item, i) =>
        i === index
          ? { ...item, siegeUId: newValue?.id || "", name: newValue?.name || "", selectedSiegeItem: newValue }
          : item
      );
      formValues({ siege: buildSiegeArray(updated) });
      return updated;
    });
  };

  const handleAddNewSiege = () =>
    setSiegeFormDataValues([...siegeFormDataValues, { siegeUId: "", numberOfUse: 0, name: "", selectedSiegeItem: null }]);

  const handleDeleteSiege = (index) => {
    const updated = siegeFormDataValues.filter((_, i) => i !== index);
    setSiegeFormDataValues(updated);
    formValues({ siege: buildSiegeArray(updated) });
  };

  // ── General field change ──────────────────────────────────────────────────

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const actualValue = type === "checkbox" ? checked : value;
    formValues({ [name]: actualValue });
    if (name === "coponCode") setCoponCode(value);
    else if (name === "typeOfCopon") setTypeOfCopon(value);
    else if (name === "reduction") setReduction(value);
    else if (name === "fromDate") setFromDate(value);
    else if (name === "toDate") setToDate(value);
    else if (name === "status") setStatus(actualValue);
  };

  const handleAutoCompleteEventChange = async (event, newValue) => {
    try {
      formValues({ eventId: newValue ? newValue?.id : "" });
      setSelectedEvent(newValue);
      setIsCinemaOrCirque(false);
      setEventDateOptions([]);
      setDateGroups([EMPTY_DATE_GROUP()]);
      setSiegeFormDataValues([]);
      if (newValue?.id) {
        fetchSiegeData(newValue.id, null);
      } else {
        setSiegeData([]);
      }
    } catch (err) {
      console.error("Error handling event change:", err);
    }
  };

  return (
    <>
      <Card>
        <MDBox p={3}>
          <MDTypography variant="h5">Informations du coupon</MDTypography>
          <MDBox mt={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  value={selectedEvent}
                  options={eventData}
                  getOptionLabel={(option) => option?.title || ""}
                  onChange={handleAutoCompleteEventChange}
                  renderInput={(params) => <TextField {...params} label="Evénement" />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormField
                  label="Code coupon *"
                  value={coponCode}
                  name="coponCode"
                  inputProps={{ type: "text" }}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <MDTypography variant="h6" textTransform="capitalize" fontWeight="regular">
                  Type (montant/pourcentage)
                </MDTypography>
                <FormControl>
                  <RadioGroup
                    row
                    aria-labelledby="typeOfCopon-label"
                    name="typeOfCopon"
                    value={typeOfCopon}
                    onChange={handleChange}
                  >
                    <FormControlLabel value="montant" control={<Radio />} label="Montant" />
                    <FormControlLabel value="pourcentage" control={<Radio />} label="Pourcentage" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormField
                  label="Réduction *"
                  value={reduction}
                  name="reduction"
                  inputProps={{ type: "number", step: "0.01" }}
                  onChange={handleChange}
                />
              </Grid>
              {/* <Grid item xs={12} sm={4}>
                <MDTypography variant="h6" textTransform="capitalize" fontWeight="regular">
                  Statut
                </MDTypography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    aria-label="status"
                    name="status"
                    value={status}
                    onChange={handleChange}
                  >
                    <FormControlLabel value={true} control={<Radio />} label="Actif" />
                    <FormControlLabel value={false} control={<Radio />} label="Inactif" />
                  </RadioGroup>
                </FormControl>
              </Grid> */}
              <Grid item xs={12} sm={4}>
                <FormField
                  label="Date de début *"
                  value={fromDate}
                  name="fromDate"
                  inputProps={{ type: "date" }}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormField
                  label="Date de fin *"
                  value={toDate}
                  name="toDate"
                  inputProps={{ type: "date" }}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </Card>

      <Card style={{ marginTop: "1rem" }}>
        <MDBox p={3}>
          <MDTypography variant="h5">Sièges</MDTypography>

          {/* ── Cinema/cirque: date groups ──────────────────────────────── */}
          {isCinemaOrCirque && (
            <>
              {dateGroups.map((group, groupIndex) => {
                const siegeOptions = siegeData.filter(
                  (s) => s.fromGeneratedId === group.selectedDateItem?.id
                );
                return (
                  <MDBox
                    key={groupIndex}
                    mt={2}
                    p={2}
                    sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}
                  >
                    {/* Date picker */}
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} sm={5}>
                        <Autocomplete
                          value={group.selectedDateItem}
                          options={eventDateOptions}
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(o, v) => o?.id === v?.id}
                          onChange={(e, newValue) => handleDateGroupChange(groupIndex, newValue)}
                          renderInput={(params) => <TextField {...params} label="Date / Séance" />}
                        />
                      </Grid>
                      {dateGroups.length > 1 && (
                        <Grid item xs="auto">
                          <Icon
                            color="error"
                            fontSize="medium"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDeleteDateGroup(groupIndex)}
                          >
                            delete
                          </Icon>
                        </Grid>
                      )}
                    </Grid>

                    {/* Siege rows */}
                    {group.selectedDateItem &&
                      group.siegeRows.map((row, rowIndex) => (
                        <MDBox mt={1} key={rowIndex}>
                          <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} sm={5}>
                              <Autocomplete
                                value={row.selectedSiegeItem}
                                options={siegeOptions}
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                                onChange={(e, newValue) =>
                                  handleGroupSiegeChange(groupIndex, rowIndex, newValue)
                                }
                                renderInput={(params) => <TextField {...params} label="Siège" />}
                              />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <FormField
                                type="number"
                                label="Nombre d'utilisations"
                                name="numberOfUse"
                                value={row.numbersOfUse || ""}
                                onChange={(e) =>
                                  handleGroupSiegeNumberOfUseChange(groupIndex, rowIndex, e)
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={2} style={{ display: "flex", alignItems: "center" }}>
                              {group.siegeRows.length - 1 === rowIndex ? (
                                <Icon
                                  color="success"
                                  fontSize="medium"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleAddSiegeToGroup(groupIndex)}
                                >
                                  add
                                </Icon>
                              ) : (
                                <Icon
                                  color="error"
                                  fontSize="medium"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleDeleteSiegeFromGroup(groupIndex, rowIndex)}
                                >
                                  delete
                                </Icon>
                              )}
                            </Grid>
                          </Grid>
                        </MDBox>
                      ))}
                  </MDBox>
                );
              })}

              <MDBox mt={2}>
                <MDButton variant="outlined" color="info" size="small" onClick={handleAddDateGroup}>
                  + Ajouter une date
                </MDButton>
              </MDBox>
            </>
          )}

          {/* ── Non-cinema/cirque: flat list ────────────────────────────── */}
          {!isCinemaOrCirque &&
            Array.isArray(siegeFormDataValues) &&
            siegeFormDataValues.map((el, index) => (
              <MDBox mt={1} key={index}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <Autocomplete
                      value={el.selectedSiegeItem}
                      options={siegeData}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(o, v) => o?.id === v?.id}
                      onChange={(event, newValue) =>
                        handleAutoCompleteSiegeChange(index, event, newValue)
                      }
                      renderInput={(params) => <TextField {...params} label="Siège" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <FormField
                      type="number"
                      label="Nombre d'utilisations"
                      name="numberOfUse"
                      value={el.numberOfUse || ""}
                      onChange={(e) => handleSiegeNumberOfUseInputChange(index, e)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2} style={{ display: "flex", alignItems: "center" }}>
                    {siegeFormDataValues.length - 1 === index &&
                    siegeFormDataValues.length < siegeData.length ? (
                      <Icon
                        color="success"
                        fontSize="medium"
                        style={{ cursor: "pointer" }}
                        onClick={handleAddNewSiege}
                      >
                        add
                      </Icon>
                    ) : siegeFormDataValues.length > 1 ? (
                      <Icon
                        color="error"
                        fontSize="medium"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDeleteSiege(index)}
                      >
                        delete
                      </Icon>
                    ) : null}
                  </Grid>
                </Grid>
              </MDBox>
            ))}
        </MDBox>
      </Card>
    </>
  );
}

export default UpdateCouponFormFields;
